import {
  Box,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useInRouterContext } from 'react-router-dom';
import { QuotePdfDownload } from './QuotePdfDownload';
import { quotePdfDownloadPath } from './quotePdfContract';

interface AssistantMarkdownProps {
  content: string;
}

/**
 * Renders an assistant message as markdown using MUI typography.
 *
 * The assistant frequently emits bold, ordered and unordered lists, inline
 * code, and the occasional link — `<Typography>` alone leaves the asterisks
 * visible. We map only the elements the assistant actually produces so the
 * bubble stays compact (no oversized headings, no margin runaway).
 *
 * GFM tables render as a compact MUI table inside a horizontally scrollable
 * container. The table resets the bubble's `wordBreak: 'break-word'`: that
 * value lets the browser shrink every column to a few characters and break
 * words anywhere, so a table wraps only at normal break opportunities (spaces,
 * hyphens) and scrolls instead. The container is a focusable, labelled region
 * so a keyboard user can scroll it in browsers that do not focus scroll
 * containers on their own (Safari).
 */
export function AssistantMarkdown({ content }: AssistantMarkdownProps) {
  const inRouter = useInRouterContext();
  const { t } = useTranslation('assistant');
  return (
    <Box
      sx={{
        wordBreak: 'break-word',
        '& > :first-of-type': { mt: 0 },
        '& > :last-child': { mb: 0 },
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => (
            <Typography variant="body2" sx={{ my: 0.5 }}>
              {children}
            </Typography>
          ),
          strong: ({ children }) => (
            <Box component="strong" sx={{ fontWeight: 600 }}>
              {children}
            </Box>
          ),
          em: ({ children }) => (
            <Box component="em" sx={{ fontStyle: 'italic' }}>
              {children}
            </Box>
          ),
          ul: ({ children }) => (
            <Box component="ul" sx={{ my: 0.5, pl: 2.5 }}>
              {children}
            </Box>
          ),
          ol: ({ children }) => (
            <Box component="ol" sx={{ my: 0.5, pl: 2.5 }}>
              {children}
            </Box>
          ),
          li: ({ children }) => (
            <Typography component="li" variant="body2" sx={{ my: 0.25 }}>
              {children}
            </Typography>
          ),
          h1: ({ children }) => (
            <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5, fontWeight: 600 }}>
              {children}
            </Typography>
          ),
          h2: ({ children }) => (
            <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5, fontWeight: 600 }}>
              {children}
            </Typography>
          ),
          h3: ({ children }) => (
            <Typography variant="subtitle2" sx={{ mt: 0.75, mb: 0.5, fontWeight: 600 }}>
              {children}
            </Typography>
          ),
          code: ({ children }) => (
            <Box
              component="code"
              sx={(theme) => ({
                px: 0.5,
                py: 0.125,
                borderRadius: 0.5,
                fontFamily: 'monospace',
                fontSize: '0.85em',
                backgroundColor:
                  theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[200],
              })}
            >
              {children}
            </Box>
          ),
          pre: ({ children }) => (
            <Box
              component="pre"
              sx={(theme) => ({
                my: 0.5,
                p: 1,
                borderRadius: 1,
                overflowX: 'auto',
                fontFamily: 'monospace',
                fontSize: '0.85em',
                backgroundColor:
                  theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[200],
              })}
            >
              {children}
            </Box>
          ),
          table: ({ children }) => (
            <TableContainer
              data-testid="assistant-markdown-table"
              role="region"
              aria-label={t('markdownTable.label')}
              tabIndex={0}
              sx={(theme) => ({
                '&:focus-visible': {
                  outline: `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: 2,
                },
                my: 0.75,
                maxWidth: '100%',
                overflowX: 'auto',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                backgroundColor: theme.palette.background.paper,
              })}
            >
              <Table
                size="small"
                sx={{
                  wordBreak: 'normal',
                  overflowWrap: 'normal',
                  // Squeezing columns to their longest word splits short ids
                  // like "TM-2546" at the hyphen; past this width the table
                  // scrolls. A long hyphenated value can still wrap there.
                  '& th, & td': { minWidth: '7em' },
                  '& tbody tr:last-of-type td': { borderBottom: 0 },
                }}
              >
                {children}
              </Table>
            </TableContainer>
          ),
          thead: ({ children }) => <TableHead>{children}</TableHead>,
          tbody: ({ children }) => <TableBody>{children}</TableBody>,
          tr: ({ children }) => <TableRow>{children}</TableRow>,
          th: ({ children, style }) => (
            <TableCell
              style={style}
              sx={(theme) => ({
                px: 1,
                py: 0.75,
                fontSize: '0.8125rem',
                fontWeight: 600,
                lineHeight: 1.35,
                verticalAlign: 'bottom',
                backgroundColor:
                  theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[200],
              })}
            >
              {children}
            </TableCell>
          ),
          td: ({ children, style }) => (
            <TableCell
              style={style}
              sx={{ px: 1, py: 0.75, fontSize: '0.8125rem', lineHeight: 1.35, verticalAlign: 'top' }}
            >
              {children}
            </TableCell>
          ),
          a: ({ href, title, children }) => {
            if (href && quotePdfDownloadPath(href)) {
              return <QuotePdfDownload href={href} fileName={title} />;
            }
            const internal =
              href?.startsWith('/') && !href.startsWith('//') && !href.includes('\\');
            if (href && internal && inRouter) {
              return (
                <Link component={RouterLink} to={href} underline="hover">
                  {children}
                </Link>
              );
            }
            return (
              <Link
                href={href}
                target={internal ? undefined : '_blank'}
                rel={internal ? undefined : 'noopener noreferrer'}
                underline="hover"
              >
                {children}
              </Link>
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </Box>
  );
}
